import React, { CSSProperties, useState } from "react";

import TimeDiff from "components/basic/timer/TimeDiff";
import useCopyDataset from "hooks/useCopyDataset";
import { Dialog } from "primereact/dialog";
import { Tooltip } from "primereact/tooltip";
import { Sparklines, SparklinesBars, SparklinesLine } from "react-sparklines";
import { T } from "services/lang/LanguageService";
import { formatTime } from "utils/time-utils";
import DatasetLineChart from "../charts/dataset-line-chart/DatasetLineChart";
import SparkLineChart from "../sparkline-advanced/SparkLineChart";
import { ISparkLineHandlerProps } from "./SparkLineTypes";

const SparkLineHandler = (props: ISparkLineHandlerProps) => {
  const [dialog, setDialog] = useState<boolean>(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);

  const labelFontSize = props.labelFontSize || "2rem";
  const sparklineWidth = props.sparklineWidth || "300px";
  const decimalPlaces = props.decimalPlaces || 0;

  const style = {
    fontSize: labelFontSize,
    fontWeight: 800,
    whiteSpace: "nowrap",
    lineHeight: "2rem",
    position: props.overlayLabel ? "absolute" : "inherit",
    top: props.vertical === "top" ? "0" : "auto",
    bottom: props.vertical === "bottom" ? "0" : "auto",
    left: props.horizontal === "left" ? "0" : "auto",
    right: props.horizontal === "right" ? "0" : "auto",
    background: "rgba(255, 255, 255, 0.6)",
  } as CSSProperties;
  const id = `sparkline_value_${(Math.random() + 1).toString(16).substring(8)}`;

  useCopyDataset(isHovered, props.title, props.dataset);

  const handleValueAndUnitTemplate = (value: number | string, unit?: string) => {
    if (!unit) {
      return value;
    }
    return `${value} ${unit}`;
  };

  let el;
  if (props.dataArray && props.dataArray.length !== 0) {
    const valuePre = props.dataArray[props.dataArray.length - 1];
    const value = valuePre?.toFixed(decimalPlaces);

    const minValue = Math.min(...props.dataArray).toFixed(decimalPlaces);
    const maxValue = Math.max(...props.dataArray).toFixed(decimalPlaces);

    el = (
      <>
        {props.advanced ? (
          <SparkLineChart
            dataArray={props.dataArray}
            advancedProps={props.advancedProps}
            lineColor={props.lineColor}
            lineWidth={props.lineWidth}
          />
        ) : (
          <div
            style={{
              lineHeight: "2rem",
              width: sparklineWidth,
              height: "40px",
              display: "inline-block",
            }}
          >
            <Sparklines data={props.dataArray} height={30} preserveAspectRatio="none">
              <SparklinesLine
                style={{
                  stroke: props.lineColor ?? "#41c3f9",
                  fill: "none",
                  strokeWidth: props.lineWidth ?? 1,
                }}
              />
              <SparklinesBars style={{ fill: props.lineColor ?? "#41c3f9", fillOpacity: ".25" }} />
            </Sparklines>
          </div>
        )}
        <div
          id={`${id}`}
          className="ea-sparkline-value"
          style={style}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {handleValueAndUnitTemplate(value, props.unit)}
        </div>

        <Tooltip target={`#${id}`} mouseTrack mouseTrackLeft={10}>
          {props.lastValueTime && (
            <div>
              <b>{T("word.spark-line.last-value-time")}:</b> {formatTime(props.lastValueTime)} (
              {`${T("word.age")}: `}
              <TimeDiff time={props.lastValueTime} />)
            </div>
          )}
          {props.showMinMax && (
            <>
              <div>
                <b>{T("word.min")}:</b> {minValue} {props.unit}
              </div>
              <div>
                <b>{T("word.max")}:</b> {maxValue} {props.unit}
              </div>
            </>
          )}
        </Tooltip>
      </>
    );
  } else {
    el = <h4>{T("word.no-data")}</h4>;
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-evenly",
          position: "relative",
          height: `${props.height ?? "auto"}px`,
        }}
        onDoubleClick={() => setDialog(true)}
      >
        {el}
      </div>

      <Dialog
        header={T("label.scada-tags-chart")}
        visible={dialog}
        style={{ width: "min(100vw, 1000px)", height: "500px" }}
        modal={false}
        onHide={() => {
          setDialog(false);
        }}
      >
        <DatasetLineChart
          title=""
          identifiers={[props.dataset]}
          showDateSelection
          dynamicHeight
          chartDialogOpen={dialog}
        />
      </Dialog>
    </>
  );
};

export default SparkLineHandler;
